"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.InBrowserStorage = exports.getStorageProviderType = exports.isProviderSupported = exports.getStorageProvider = void 0;
const objects_1 = require("./objects");
class InMemoryStorage {
    constructor() {
        this.values = {};
        this.name = 'inMemory';
        this.clear = () => (this.values = {});
        /** This probably is not going to work right, but should be good enough.. */
        this.key = (index) => Object.keys(this.values)[index];
        this.getItem = (key) => this.values[key];
        this.setItem = (key, value) => (this.values[key] = value);
        this.removeItem = (key) => delete this.values[key];
    }
    get length() {
        return Object.keys(this.values).length;
    }
    /** String representation of stored values. */
    toString() {
        return JSON.stringify(this.values);
    }
}
const inMemProvider = new InMemoryStorage();
const loadedProviders = {
    inMemory: inMemProvider,
    sessionStorage: undefined,
    localStorage: undefined,
};
const isStorage = (value) => !!value;
/**
 * Gets the requested storage provider if it exists and is enabled in browser; falls back to in-memory if not.
 *
 * @param providerType - the preferred storage provider you want
 * @param fallbacks - ordered list of storage provider to fallback to if specified not available; will provide inMemory if none others are available
 * @returns storage provider
 */
const getStorageProvider = (providerType = 'localStorage', fallbacks = ['sessionStorage', 'inMemory']) => {
    let provider = loadedProviders[providerType];
    if (provider) {
        return provider;
    }
    try {
        provider = window[providerType];
    }
    catch (ex) {
        // ignore; this is part of the test to find a usable provider
    }
    let supportsGetSet = provider && typeof provider.getItem === 'function' && typeof provider.setItem === 'function';
    if (isStorage(provider) && supportsGetSet) {
        try {
            // ensure it actually works (some docs say browsers can have a provider defined w/o it actually working)
            const key = '_t';
            const val = 't';
            provider.setItem(key, val);
            if (provider.getItem(key) !== val) {
                throw new Error('Provider not supported.');
            }
            provider.removeItem(key); // clear test value
        }
        catch (ex) {
            supportsGetSet = false;
        }
    }
    if (!isStorage(provider) || !supportsGetSet) {
        const nextFallback = fallbacks[0] || 'inMemory';
        // we don't use logging to avoid circular
        console.info(`[built] Storage Provider '${providerType}' not supported. Trying '${nextFallback}' instead.`);
        provider = (0, exports.getStorageProvider)(nextFallback, fallbacks.slice(1));
    }
    loadedProviders[providerType] = provider;
    return provider;
};
exports.getStorageProvider = getStorageProvider;
/**
 * Gets if the requested storage provider is supported in current browser context.
 *
 * @param providerType - the preferred storage provider you want
 * @returns true if the provider is supported; false otherwise
 */
const isProviderSupported = (providerType) => (0, exports.getStorageProviderType)((0, exports.getStorageProvider)(providerType)) === providerType;
exports.isProviderSupported = isProviderSupported;
/**
 * Use this to inspect the type of provider.
 *
 * Tests for equivalence to known providers because they don't provide typing for each.
 *
 * @param provider - provider to check
 * @returns type of provider
 */
const getStorageProviderType = (provider) => {
    try {
        if (provider === window.localStorage) {
            return 'localStorage';
        }
    }
    catch (ex) { }
    try {
        if (provider === window.sessionStorage) {
            return 'sessionStorage';
        }
    }
    catch (ex) { }
    if (provider === inMemProvider) {
        return 'inMemory';
    }
    return 'unknown';
};
exports.getStorageProviderType = getStorageProviderType;
/**
 * Can use this to more easily interface with data stored in the browser in one form or another.
 */
class InBrowserStorage {
    /**
     *
     * @param storageKey - the key to use for storing the data in the storage provider
     * @param defaultData - the default data to use if none is found in storage; required so default can match expected type
     * @param options - options for configuring the storage provider
     */
    constructor(storageKey, defaultData, options = {}) {
        this.rootKey = storageKey;
        this.provider = (0, exports.getStorageProvider)(options.provider || 'localStorage');
        this.options = options;
        this._defaultData = defaultData;
    }
    /** Gets a copy of the configured default data. */
    get defaultData() {
        // we clone so that it is never mutated (might otherwise be in failures to get, when we return it)
        return (0, objects_1.deepClone)(this._defaultData);
    }
    /**
     * Get current data from the configured storage provider.
     *
     * **NOTE**: We do not validate, so if someone has messed with the value directly in storage or some other API, you may get unexpected results.
     */
    get() {
        const rawValue = this.provider.getItem(this.rootKey);
        if (!rawValue) {
            return this.defaultData;
        }
        try {
            const parsedData = JSON.parse(rawValue);
            return parsedData;
        }
        catch (ex) {
            // not using logging.warn to avoid circular reference
            console.warn(`Could not parse data from storage for key '${this.rootKey}'. Raw Value: '${rawValue}'.`, ex);
            return this.defaultData;
        }
    }
    /**
     * Save data to the configured storage provider.
     *
     * @param data - updated data to save
     */
    save(data) {
        try {
            const rawValue = JSON.stringify(data);
            if (Object.keys(data).length === 0) {
                this.provider.removeItem(this.rootKey);
                return;
            }
            this.provider.setItem(this.rootKey, rawValue);
        }
        catch (ex) {
            // not using logging.warn to avoid circular reference
            console.warn(`Problem saving data for key '${this.rootKey}' to storage.`, data, ex);
        }
    }
    /**
     * Save a section of data to the configured storage provider.
     *
     * @param sectionName - name of section (property on root data object)
     * @param sectionData - data to save for section; if undefined, will delete section
     */
    saveSection(sectionName, sectionData) {
        const storageData = this.get();
        if (sectionData === undefined) {
            delete storageData[sectionName];
        }
        else {
            storageData[sectionName] = sectionData;
        }
        this.save(storageData);
    }
}
exports.InBrowserStorage = InBrowserStorage;
